import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { assetsApi } from '../../lib/api';
import { toast } from 'sonner';

const assetTypes = [
  { value: 'hardware', label: 'Hardware' },
  { value: 'software', label: 'Software' },
  { value: 'dados', label: 'Dados' },
  { value: 'servico', label: 'Serviço' },
  { value: 'pessoas', label: 'Pessoas' },
  { value: 'instalacao', label: 'Instalação' },
];

const confidentialityLevels = [
  { value: 'publica', label: 'Pública' },
  { value: 'interna', label: 'Interna' },
  { value: 'confidencial', label: 'Confidencial' },
  { value: 'estritamente_confidencial', label: 'Estritamente Confidencial' },
];

const integrityLevels = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Crítica' },
];

const availabilityLevels = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Crítica' },
];

const operationalStatuses = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'descontinuado', label: 'Descontinuado' },
];

const initialFormData = {
  name: '',
  description: '',
  asset_type: 'hardware',
  category: '',
  confidentiality: 'interna',
  integrity: 'media',
  availability: 'media',
  estimated_value: '',
  department: '',
  physical_location: '',
  logical_location: '',
  vendor: '',
  model: '',
  version: '',
  serial_number: '',
  acquisition_date: '',
  end_of_life: '',
  end_of_support: '',
  operational_status: 'ativo',
  ip_address: '',
  hostname: '',
  tags: '',
  notes: '',
};

export default function AssetForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const copyId = searchParams.get('copy');
  
  const isEdit = !!id;
  const [isLoading, setIsLoading] = useState(isEdit || !!copyId);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (isEdit) {
      loadAsset(id);
    } else if (copyId) {
      loadAsset(copyId);
    }
  }, [id, copyId]);

  const loadAsset = async (assetId) => {
    try {
      const response = await assetsApi.getById(assetId);
      const asset = response.data;
      setFormData({
        name: copyId ? `${asset.name} (Cópia)` : asset.name,
        description: asset.description || '',
        asset_type: asset.asset_type,
        category: asset.category || '',
        confidentiality: asset.confidentiality,
        integrity: asset.integrity,
        availability: asset.availability,
        estimated_value: asset.estimated_value || '',
        department: asset.department || '',
        physical_location: asset.physical_location || '',
        logical_location: asset.logical_location || '',
        vendor: asset.vendor || '',
        model: asset.model || '',
        version: asset.version || '',
        serial_number: copyId ? '' : (asset.serial_number || ''),
        acquisition_date: asset.acquisition_date || '',
        end_of_life: asset.end_of_life || '',
        end_of_support: asset.end_of_support || '',
        operational_status: asset.operational_status,
        ip_address: copyId ? '' : (asset.ip_address || ''),
        hostname: copyId ? '' : (asset.hostname || ''),
        tags: asset.tags?.join(', ') || '',
        notes: asset.notes || '',
      });
    } catch (error) {
      toast.error('Erro ao carregar ativo');
      navigate('/assets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('O nome do ativo é obrigatório');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        tags: formData.tags ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };

      if (isEdit) {
        await assetsApi.update(id, payload);
        toast.success('Ativo atualizado com sucesso');
      } else {
        const response = await assetsApi.create(payload);
        toast.success('Ativo criado com sucesso');
        navigate(`/assets/${response.data.asset_id}`);
        return;
      }
      navigate(`/assets/${id}`);
    } catch (error) {
      toast.error(isEdit ? 'Erro ao atualizar ativo' : 'Erro ao criar ativo');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout title={isEdit ? 'Editar Ativo' : 'Novo Ativo'}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/assets')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span>{isEdit ? 'Editar Ativo' : 'Novo Ativo'}</span>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="asset-form">
        {/* Basic Info */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg">Informação Básica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome do Ativo *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Ex: Servidor Principal DC-01"
                  required
                  data-testid="asset-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="asset_type">Tipo de Ativo *</Label>
                <Select
                  value={formData.asset_type}
                  onValueChange={(value) => handleChange('asset_type', value)}
                >
                  <SelectTrigger id="asset_type" data-testid="asset-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assetTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descrição detalhada do ativo..."
                rows={3}
                data-testid="asset-description-input"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  placeholder="Ex: Servidor, Aplicação de Negócio"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Departamento</Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  placeholder="Ex: TI, Segurança, Financeiro"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Classification */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg">Classificação de Segurança (CIA)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="confidentiality">Confidencialidade</Label>
                <Select
                  value={formData.confidentiality}
                  onValueChange={(value) => handleChange('confidentiality', value)}
                >
                  <SelectTrigger id="confidentiality" data-testid="confidentiality-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {confidentialityLevels.map((level) => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="integrity">Integridade</Label>
                <Select
                  value={formData.integrity}
                  onValueChange={(value) => handleChange('integrity', value)}
                >
                  <SelectTrigger id="integrity" data-testid="integrity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {integrityLevels.map((level) => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="availability">Disponibilidade</Label>
                <Select
                  value={formData.availability}
                  onValueChange={(value) => handleChange('availability', value)}
                >
                  <SelectTrigger id="availability" data-testid="availability-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availabilityLevels.map((level) => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimated_value">Valor Estimado (€)</Label>
                <Input
                  id="estimated_value"
                  type="number"
                  value={formData.estimated_value}
                  onChange={(e) => handleChange('estimated_value', e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Info */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg">Informação Técnica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vendor">Fabricante/Fornecedor</Label>
                <Input
                  id="vendor"
                  value={formData.vendor}
                  onChange={(e) => handleChange('vendor', e.target.value)}
                  placeholder="Ex: Dell, Microsoft, SAP"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Modelo</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => handleChange('model', e.target.value)}
                  placeholder="Ex: PowerEdge R750"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="version">Versão</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => handleChange('version', e.target.value)}
                  placeholder="Ex: 2023, v10.0"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="serial_number">Número de Série</Label>
                <Input
                  id="serial_number"
                  value={formData.serial_number}
                  onChange={(e) => handleChange('serial_number', e.target.value)}
                  placeholder="Ex: ABC123456"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ip_address">Endereço IP</Label>
                <Input
                  id="ip_address"
                  value={formData.ip_address}
                  onChange={(e) => handleChange('ip_address', e.target.value)}
                  placeholder="Ex: 10.0.1.10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hostname">Hostname</Label>
                <Input
                  id="hostname"
                  value={formData.hostname}
                  onChange={(e) => handleChange('hostname', e.target.value)}
                  placeholder="Ex: srv-dc-01.empresa.local"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location and Lifecycle */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg">Localização e Ciclo de Vida</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="physical_location">Localização Física</Label>
                <Input
                  id="physical_location"
                  value={formData.physical_location}
                  onChange={(e) => handleChange('physical_location', e.target.value)}
                  placeholder="Ex: Datacenter Principal - Rack A1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="logical_location">Localização Lógica</Label>
                <Input
                  id="logical_location"
                  value={formData.logical_location}
                  onChange={(e) => handleChange('logical_location', e.target.value)}
                  placeholder="Ex: VLAN 10, Domínio empresa.local"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="operational_status">Estado Operacional</Label>
                <Select
                  value={formData.operational_status}
                  onValueChange={(value) => handleChange('operational_status', value)}
                >
                  <SelectTrigger id="operational_status" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {operationalStatuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="acquisition_date">Data de Aquisição</Label>
                <Input
                  id="acquisition_date"
                  type="date"
                  value={formData.acquisition_date}
                  onChange={(e) => handleChange('acquisition_date', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_of_life">Fim de Vida</Label>
                <Input
                  id="end_of_life"
                  type="date"
                  value={formData.end_of_life}
                  onChange={(e) => handleChange('end_of_life', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_of_support">Fim de Suporte</Label>
                <Input
                  id="end_of_support"
                  type="date"
                  value={formData.end_of_support}
                  onChange={(e) => handleChange('end_of_support', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg">Informação Adicional</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (separadas por vírgula)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => handleChange('tags', e.target.value)}
                placeholder="Ex: crítico, produção, windows"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Observações gerais sobre o ativo..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/assets')}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSaving} data-testid="save-asset-btn">
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            {isEdit ? 'Guardar Alterações' : 'Criar Ativo'}
          </Button>
        </div>
      </form>
    </Layout>
  );
}
